import React from 'react';
import Icon from '../../../components/AppIcon';

const AlertsPanel = ({ clients }) => {
  const paymentFailures = clients?.filter(c => {
    if (!c?.nextBillingDate) return true;
    const daysUntilBilling = Math.floor(
      (new Date(c?.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilBilling < 0;
  });

  const complianceViolations = clients?.filter(c => (c?.complianceScore || 0) < 70);
  
  const setupBottlenecks = clients?.filter(c => {
    const progress = c?.setupProgress || 0;
    return progress < 50 && progress > 0;
  });

  const suspendedAccounts = clients?.filter(c => c?.subscriptionStatus === 'suspended');

  if (
    paymentFailures?.length === 0 &&
    complianceViolations?.length === 0 &&
    setupBottlenecks?.length === 0 &&
    suspendedAccounts?.length === 0
  ) {
    return (
      <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <p className="text-sm font-medium text-foreground">
            All systems operational - No critical alerts at this time
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="AlertTriangle" size={20} className="text-warning" />
        <h2 className="text-lg font-bold text-foreground">Critical Alerts - Immediate Intervention Required</h2>
      </div>

      <div className="space-y-3">
        {/* Payment Failures */}
        {paymentFailures?.length > 0 && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="CreditCard" size={18} className="text-error mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground mb-1">Payment Failures</h3>
                <p className="text-sm text-muted-foreground">
                  {paymentFailures?.length} client{paymentFailures?.length > 1 ? 's have' : ' has'} overdue payments requiring immediate billing intervention
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {paymentFailures?.slice(0, 3)?.map(client => (
                    <span key={client?.id} className="text-xs bg-error/20 text-error px-2 py-1 rounded">
                      {client?.companyName}
                    </span>
                  ))}
                  {paymentFailures?.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{paymentFailures?.length - 3} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Violations */}
        {complianceViolations?.length > 0 && (
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={18} className="text-warning mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground mb-1">Compliance Violations</h3>
                <p className="text-sm text-muted-foreground">
                  {complianceViolations?.length} client{complianceViolations?.length > 1 ? 's have' : ' has'} compliance scores below 70% - PNG Employment Act adherence at risk
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {complianceViolations?.slice(0, 3)?.map(client => (
                    <span key={client?.id} className="text-xs bg-warning/20 text-warning px-2 py-1 rounded">
                      {client?.companyName} ({client?.complianceScore}%)
                    </span>
                  ))}
                  {complianceViolations?.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{complianceViolations?.length - 3} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Setup Bottlenecks */}
        {setupBottlenecks?.length > 0 && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={18} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground mb-1">Setup Bottlenecks</h3>
                <p className="text-sm text-muted-foreground">
                  {setupBottlenecks?.length} client{setupBottlenecks?.length > 1 ? 's are' : ' is'} stalled in onboarding process (below 50% completion)
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {setupBottlenecks?.slice(0, 3)?.map(client => (
                    <span key={client?.id} className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                      {client?.companyName} ({client?.setupProgress}%)
                    </span>
                  ))}
                  {setupBottlenecks?.length > 3 && (
                    <span className="text-xs text-muted-foreground">+{setupBottlenecks?.length - 3} more</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suspended Accounts */}
        {suspendedAccounts?.length > 0 && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Ban" size={18} className="text-error mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-foreground mb-1">Suspended Accounts</h3>
                <p className="text-sm text-muted-foreground">
                  {suspendedAccounts?.length} account{suspendedAccounts?.length > 1 ? 's are' : ' is'} currently suspended and require resolution
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;